<template>
  <main class="bg-gray-200 min-h-screen">
    <header>
      <div
        class="bg-white border-b border-gray-200 px-4 py-4 sm:flex sm:items-center sm:justify-between sm:px-6 lg:px-8"
      >
        <div class="flex-1 min-w-0">
          <h1 class="text-lg font-medium leading-6 text-gray-900 sm:truncate">
            {{ $t('invoice_view_title') }}
          </h1>
        </div>
        <div class="mt-4 flex sm:mt-0 sm:ml-4 space-x-2">
          <sqr-button
            :label="$t('buttons_back')"
            color="white"
            size="medium"
            @click="$router.back()"
          >
            <template slot="icon">
              <ArrowLeftIcon class="inline w-4 h-4 stroke-current" />
            </template>
          </sqr-button>

          <router-link :to="{ name: 'invoice-clone', params: { aid, id } }">
            <sqr-button :label="$t('invoice_clone')" color="white">
              <template slot="icon">
                <DuplicateIcon class="inline w-4 h-4 stroke-current" />
              </template>
            </sqr-button>
          </router-link>

          <sqr-button
            :label="$t('invoice_print')"
            color="white"
            @click="print()"
            :loading="previewing"
            :disabled="loading"
          >
            <template slot="icon">
              <PrinterIcon class="inline w-4 h-4 stroke-current" />
            </template>
          </sqr-button>

          <router-link :to="{ name: 'invoice-email', params: { aid, id } }">
            <sqr-button
              :label="$t('invoice_email')"
              color="blue"
              light
              :disabled="loading"
            >
              <template slot="icon">
                <MailIcon class="inline w-4 h-4 stroke-current" />
              </template>
            </sqr-button>
          </router-link>

          <sqr-button
            :label="$t('invoice_download')"
            color="green"
            @click="download()"
            :loading="previewing"
            :disabled="loading"
          >
            <template slot="icon">
              <DownloadIcon class="inline w-4 h-4 stroke-current" />
            </template>
          </sqr-button>
        </div>
      </div>
      <sqr-progress v-show="loading" />
      <sqr-error-banner :error="loadError" @retry="load({ reload: true })" />
    </header>
    <section class="section" v-if="adBlockIssue">
      <div class="container">
        <div class="columns is-centered">
          <div class="column is-12-mobile is-9-tablet is-7-desktop">
            <div class="notification is-warning">
              {{ $t('invoice_edit_ad_block_issue') }}
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="section">
      <div class="container">
        <invoice-preview :invoice="invoice" />
      </div>
    </section>
    <section class="section"></section>
  </main>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import {
  ArrowLeftIcon,
  DownloadIcon,
  DuplicateIcon,
  MailIcon,
  PrinterIcon
} from '@vue-hero-icons/outline';

import SqrButton from '@/components/SqrButton';

import invoiceRender from '../pdfs/invoice';

const pdfMake = require('pdfmake/build/pdfmake.js');
const pdfFonts = require('../pdfs/vfs_fonts.js');
pdfMake.fonts = {
  Helvetica: {
    normal: 'Helvetica-Regular.ttf',
    bold: 'Helvetica-Bold.ttf'
  }
};
pdfMake.vfs = pdfFonts.pdfMake.vfs;

// import RenderWorker from '../pdfs/render3.worker.js';
// const render = new RenderWorker();

export default {
  name: 'InvoiceView',
  components: {
    InvoicePreview: () =>
      import(
        /* webpackChunkName: "InvoicePreview" */ '../components/InvoicePreview'
      ),
    SqrButton,
    ArrowLeftIcon,
    DuplicateIcon,
    PrinterIcon,
    MailIcon,
    DownloadIcon
  },
  props: {
    id: String
  },
  computed: {
    ...mapGetters('auth', ['uid']),
    ...mapGetters('account', ['accountId', 'account']),
    ...mapGetters('invoice', ['invoiceId', 'invoice', 'loading', 'loadError'])
  },
  data() {
    return {
      previewing: false,
      adBlockIssue: false
    };
  },
  mounted() {
    this.$a().logEvent('invoice_view');
    const accountId = this.accountId;
    const id = this.id;
    this.subscribe({ accountId, id });
  },
  methods: {
    ...mapActions('invoice', ['subscribe']),
    async preview() {
      const model = await invoiceRender(
        this.invoice,
        this,
        this.$i18n && this.$i18n.current,
        this.uid
      );
      const pdf = pdfMake.createPdf(model.doc, model.tableLayouts);
      const child = window.open('', '');
      pdf.open({}, child);
      setTimeout(() => {
        this.adBlockIssue = child.closed;
      }, 1000);
    },
    async print() {
      const model = await invoiceRender(
        this.invoice,
        this,
        this.$i18n && this.$i18n.current,
        this.uid
      );
      const pdf = pdfMake.createPdf(model.doc, model.tableLayouts);
      const child = window.open('', '');
      pdf.print({}, child);
      setTimeout(() => {
        this.adBlockIssue = child.closed;
      }, 1000);
    },
    async download() {
      const model = await invoiceRender(
        this.invoice,
        this,
        this.$i18n && this.$i18n.current,
        this.uid
      );
      const pdf = pdfMake.createPdf(model.doc, model.tableLayouts);
      pdf.download(this.invoice.number + '.pdf');
    }
  },
  metaInfo() {
    return {
      title: this.$t('invoice_view_title_alt', this.invoice)
    };
  }
};
</script>
